/**
 * Add funds' content (the design system's `DepositModal`): GET /v1/me/balance, the top-up packs
 * (`balancePurchases`), Stripe's checkout and its return, and POST /v1/me/gift-codes/redeem. The
 * open state and the URL's requests are `lib/deposit.ts`; the modal is `components/StoreDeposit.tsx`.
 */
import {
  defaultAccountHeaderCopy,
  formatAmountWithUnit,
  heldForRevealsLine,
  USD,
} from "@loot-os/api-contract";
import type {
  DepositModalProps,
  DepositPacksState,
  GiftCodeField,
} from "@loot-os/design-system/domain/DepositModal";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { track } from "./analytics.js";
import { spendableCents } from "./balance.js";
import { useCurrency } from "./currency.js";
import {
  depositRequestOf,
  setDepositOpen,
  useDepositOpen,
  withoutDepositRequest,
} from "./deposit.js";
import { giftCodeErrorMessage, useRedeemGiftCode } from "./giftCodes.js";
import {
  balancePacksQueryOptions,
  lootMeQueryOptions,
  PURCHASE_POLL_LIMIT,
  purchasePollQueryOptions,
  qk,
} from "./queries.js";
import { queryClient } from "./queryClient.js";
import { useBalance, useShopper, useStartCheckout } from "./shopper.js";
import { useTenant } from "./tenant.js";
import { useTerminology } from "./terminology.js";
import { showToast } from "./toasts.js";

type PurchaseState = "confirming" | "completed" | "failed" | "canceled";

/** Add funds, open or closed, filled for the signed-in shopper. */
export function useDepositModalContent(): DepositModalProps {
  const open = useDepositOpen();
  const { sessionToken } = useShopper();
  const { hasFeature } = useTenant();
  const currency = useCurrency();
  // The balance's words are the account header's, which says the same of the same balance.
  const terms = useTerminology();
  const words = defaultAccountHeaderCopy(terms).header;
  const unit = currency.namePlural;
  const balance = useBalance().data ?? null;
  // GET /v1/me carries the lock; the server refuses balance purchases and redemptions on its own,
  // so this is the shopper-facing half of the same state.
  const me = useQuery({ ...lootMeQueryOptions(), enabled: !!sessionToken && open }).data ?? null;
  const locked = me?.locked ?? false;
  const [error, setError] = useState<string | null>(null);

  const purchase = usePurchaseReturn(open);
  const giftCode = useGiftCodeField(!balance || locked);

  const canBuy = hasFeature("balancePurchases");
  const packs = useQuery({
    ...balancePacksQueryOptions(),
    enabled: canBuy && !!sessionToken && open,
  }).data;
  const checkout = useStartCheckout();
  const buyingPackId =
    checkout.isPending || checkout.isSuccess ? (checkout.variables ?? null) : null;

  const deposit: DepositPacksState = !canBuy
    ? {
        status: "unavailable",
        message: `Buying ${unit} isn't available in this store. You can still redeem a gift code.`,
      }
    : !packs
      ? { status: "loading" }
      : packs.length === 0
        ? { status: "unavailable", message: `No ${unit} are on sale right now.` }
        : {
            status: "ready",
            label: "Top-up packs",
            packs: packs.map((pack) => ({
              id: pack.id,
              name: pack.name,
              creditedCents: pack.creditedCents,
              priceCents: pack.priceCents,
              onBuy: () => {
                setError(null);
                checkout.mutate(pack.id, {
                  onError: (err) =>
                    setError(
                      err.message === "account_locked"
                        ? "This account is locked. Contact support."
                        : err.message === "fraud_declined"
                          ? "This purchase could not be completed. Contact support."
                          : "Could not start checkout. Try again.",
                    ),
                });
              },
            })),
            priceCurrency: USD,
            afterLabel: "Balance after",
            buyLabel: (price) => `Deposit ${price}`,
            buying: buyingPackId,
            buyingLabel: "Redirecting…",
            disabled: locked || buyingPackId !== null || purchase.state === "confirming",
          };

  const purchaseNotice = purchaseLine(purchase.state, unit);
  const notices: DepositModalProps["notices"] = [
    ...(locked
      ? [
          {
            id: "locked",
            tone: "danger" as const,
            text: `This account is locked, so you can't buy ${unit} or redeem codes. Contact support to get it lifted.`,
          },
        ]
      : []),
    ...(error ? [{ id: "error", tone: "danger" as const, text: error }] : []),
    ...(purchaseNotice ? [{ id: "purchase", ...purchaseNotice }] : []),
  ];

  return {
    open,
    onOpenChange: (next) => {
      setDepositOpen(next);
      if (!next) {
        setError(null);
        purchase.dismiss();
      }
    },
    title: "Add funds",
    currency,
    balance: {
      label: words.balance,
      cents: balance ? spendableCents(balance) : null,
      held:
        balance && balance.heldCents > 0
          ? heldForRevealsLine(formatAmountWithUnit(balance.heldCents, currency), terms)
          : undefined,
    },
    notices,
    deposit,
    giftCode,
  };
}

function purchaseLine(
  state: PurchaseState | null,
  unit: string,
): Omit<DepositModalProps["notices"][number], "id"> | null {
  switch (state) {
    case "confirming":
      return { tone: "neutral", text: "Confirming your purchase…" };
    case "completed":
      return { tone: "success", text: `Purchase complete — ${unit} added.` };
    case "failed":
      return { tone: "danger", text: FAILED_TEXT };
    case "canceled":
      return { tone: "neutral", text: "Checkout canceled." };
    default:
      return null;
  }
}

const FAILED_TEXT =
  "We couldn't confirm the purchase yet. If you paid, the balance updates as soon as the payment settles.";

/**
 * What the URL asks of Add funds (`depositRequestOf`): `?deposit=1`, the old Wallet tab, or
 * Stripe's return to `/account?purchase=success&session_id=cs_...` (or cancelled). Each is read
 * once the session is known, then taken out of the URL (replace) so a reload or Back doesn't repeat
 * it; the ref keeps StrictMode's double effect from reading it twice. Signed out, the request is
 * dropped: there is nothing to add funds to. A paid return is polled until it leaves "pending"
 * (webhooks usually land within seconds), a 401, or the poll limit (purchasePollQueryOptions);
 * the outcome shows in the modal, or as a toast when the shopper closed it meanwhile.
 */
function usePurchaseReturn(open: boolean) {
  const { user, authPending, sessionToken } = useShopper();
  const navigate = useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });
  const searchStr = useLocation({ select: (location) => location.searchStr });
  const [state, setState] = useState<PurchaseState | null>(null);
  const [checkoutSessionId, setCheckoutSessionId] = useState<string | null>(null);
  const handledRef = useRef<string | null>(null);
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    const request = depositRequestOf(searchStr);
    if (!request || authPending) return;
    // Signed in, wait for the session token before acting on it (a checkout return polls with it).
    if (user && !sessionToken) return;
    const key = `${pathname}${searchStr}`;
    if (handledRef.current === key) return;
    handledRef.current = key;
    void navigate({ href: withoutDepositRequest(pathname, searchStr), replace: true });
    if (!user) return;
    setDepositOpen(true);
    const checkout = request.checkout;
    if (checkout?.status === "cancelled") {
      track("checkout_returned", { status: "cancelled" });
      setState("canceled");
    } else if (checkout?.status === "success") {
      setState("confirming");
      setCheckoutSessionId(checkout.sessionId);
    }
  }, [pathname, searchStr, authPending, user, sessionToken, navigate]);

  const poll = useQuery({
    ...purchasePollQueryOptions(checkoutSessionId ?? "none"),
    enabled: !!checkoutSessionId && state === "confirming",
  });
  useEffect(() => {
    if (!checkoutSessionId || state !== "confirming") return;
    const result = poll.data;
    if (!result) return;
    const terminal =
      result.kind === "unauthorized" ||
      (result.kind === "status" && result.status.status !== "pending");
    const timedOut = !terminal && result.attempt >= PURCHASE_POLL_LIMIT;
    if (!terminal && !timedOut) return;
    const outcome =
      result.kind === "status" && result.status.status === "completed" ? "completed" : "failed";
    track("checkout_returned", { status: outcome });
    setState(outcome);
    setCheckoutSessionId(null);
    // The balance may have moved even on the timeout path.
    void queryClient.invalidateQueries({ queryKey: qk.balance });
    if (!openRef.current) {
      showToast(
        outcome === "completed"
          ? { kind: "success", title: "Purchase complete." }
          : { kind: "error", title: FAILED_TEXT },
      );
    }
  }, [checkoutSessionId, state, poll.data]);

  return {
    state,
    /** Closing the modal clears an outcome already shown; a purchase still confirming stays. */
    dismiss: () => setState((current) => (current === "confirming" ? current : null)),
  };
}

/**
 * The gift code field: POST /v1/me/gift-codes/redeem, then what it added or why it didn't. A
 * refusal clears as soon as the shopper types again.
 */
function useGiftCodeField(disabled: boolean): GiftCodeField {
  const currency = useCurrency();
  const redeem = useRedeemGiftCode();
  const [code, setCode] = useState("");
  const [outcome, setOutcome] = useState<GiftCodeField["outcome"]>(null);
  return {
    label: "Gift code",
    placeholder: "XXXX-XXXX-XXXX-XXXX",
    value: code,
    onChange: (value) => {
      setCode(value);
      if (outcome?.tone === "danger") setOutcome(null);
    },
    onSubmit: () => {
      const typed = code.trim();
      if (!typed) return;
      setOutcome(null);
      redeem.mutate(typed, {
        onSuccess: (redemption) => {
          setCode("");
          setOutcome({
            tone: "success",
            text: `Code redeemed: ${formatAmountWithUnit(redemption.amountCents, currency)} added to your balance.`,
          });
        },
        onError: (err) => setOutcome({ tone: "danger", text: giftCodeErrorMessage(err) }),
      });
    },
    submitLabel: redeem.isPending ? "Redeeming…" : "Redeem",
    pending: redeem.isPending,
    disabled,
    outcome,
  };
}
